import { useRef, useEffect, useCallback, useState } from "react";

/**
 * Custom hook to manage table data processing with Web Worker
 */
export const useTableWorker = () => {
    const workerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const pendingOperationsRef = useRef(new Set());
    const isTerminatingRef = useRef(false);

    // Initialize worker
    useEffect(() => {
        isTerminatingRef.current = false;
        
        try {
            workerRef.current = new Worker(
                new URL("../workers/ReportTableWorker.js", import.meta.url)
            );

            workerRef.current.onerror = (error) => {
                console.error("Worker initialization error:", error);
                setError("Worker initialization failed");
            };
        } catch (error) {
            console.error("Failed to create worker:", error);
            setError("Failed to create worker");
        }

        return () => {
            isTerminatingRef.current = true;
            
            // Reject all pending operations
            pendingOperationsRef.current.forEach(reject => {
                reject(new Error("Worker terminated"));
            });
            pendingOperationsRef.current.clear();
            
            if (workerRef.current) {
                try {
                    workerRef.current.terminate();
                    workerRef.current = null;
                } catch (error) {
                    console.error("Error terminating worker:", error);
                }
            }
        };
    }, []);

    // Process data with worker
    const processData = useCallback(
        (data, sorting, currentPage, itemsPerPage, filters = [], needFullData = false) => {
            return new Promise((resolve, reject) => {
                // Check if worker is being terminated or already terminated
                if (isTerminatingRef.current || !workerRef.current) {
                    resolve({
                        paginatedData: [],
                        totalItems: 0,
                        totalPages: 0,
                        currentPage,
                        itemsPerPage,
                        startIndex: 0,
                        endIndex: 0,
                    });
                    return;
                }
                
                if (!data || data.length === 0) {
                    resolve({
                        paginatedData: [],
                        totalItems: 0,
                        totalPages: 0,
                        currentPage,
                        itemsPerPage,
                        startIndex: 0,
                        endIndex: 0,
                    });
                    return;
                }
                
                // Add this operation to pending set
                pendingOperationsRef.current.add(reject);

                setIsLoading(true);
                setError(null);

                // Convert sorting format for worker.js
                let sortType = undefined;
                if (sorting && sorting.length > 0) {
                    const sort = sorting[0];
                    sortType = {
                        accessorKey: sort.accessorKey || sort.id,
                        desc: sort.desc || false,
                        sortMethod: sort.sortMethod || "default",
                    };
                }

                // Convert filters format for worker.js
                const filterValue = filters.map((filter) => ({
                    accessorKey: filter.id || filter.accessorKey,
                    filterValue: filter.value || filter.filterValue,
                    columnName:
                        filter.columnName || filter.id || filter.accessorKey,
                }));

                const handleMessage = (e) => {
                    const {
                        paginatedData,
                        totalFilteredCount,
                        filteredData,
                        pageNumber,
                    } = e.data;

                    try {
                        const result = {
                            paginatedData,
                            totalItems: totalFilteredCount,
                            totalPages: Math.ceil(
                                totalFilteredCount / itemsPerPage
                            ),
                            currentPage: pageNumber || currentPage, // Use adjusted page if provided
                            itemsPerPage,
                            startIndex:
                                ((pageNumber || currentPage) - 1) *
                                    itemsPerPage +
                                1,
                            endIndex: Math.min(
                                (pageNumber || currentPage) * itemsPerPage,
                                totalFilteredCount
                            ),
                            filteredData,
                            pageAdjusted: pageNumber !== currentPage, // Flag to indicate page was adjusted
                        };

                        setIsLoading(false);
                        setError(null);
                        
                        // Remove from pending operations
                        pendingOperationsRef.current.delete(reject);
                        
                        if (workerRef.current && !isTerminatingRef.current) {
                            workerRef.current.removeEventListener(
                                "message",
                                handleMessage
                            );
                        }
                        resolve(result);
                    } catch (error) {
                        console.error(
                            "Processing worker response error:",
                            error
                        );
                        setError(error.message);
                        setIsLoading(false);
                        
                        // Remove from pending operations
                        pendingOperationsRef.current.delete(reject);
                        
                        if (workerRef.current && !isTerminatingRef.current) {
                            workerRef.current.removeEventListener(
                                "message",
                                handleMessage
                            );
                        }
                        reject(error);
                    }
                };

                const handleError = (error) => {
                    console.error("Worker error:", error);
                    setError(error.message);
                    setIsLoading(false);
                    
                    // Remove from pending operations
                    pendingOperationsRef.current.delete(reject);
                    
                    if (workerRef.current && !isTerminatingRef.current) {
                        workerRef.current.removeEventListener(
                            "message",
                            handleMessage
                        );
                        workerRef.current.removeEventListener("error", handleError);
                    }
                    reject(error);
                };

                // Double-check worker is still valid before setting up listeners
                if (!workerRef.current || isTerminatingRef.current) {
                    pendingOperationsRef.current.delete(reject);
                    reject(new Error("Worker not available"));
                    return;
                }

                try {
                    workerRef.current.addEventListener("message", handleMessage);
                    workerRef.current.addEventListener("error", handleError);

                    workerRef.current.postMessage({
                        page: currentPage,
                        pageSize: itemsPerPage,
                        array: data,
                        sortType,
                        filterValue,
                        needFullData, // Only request full data when needed (e.g., for export)
                    });
                } catch (error) {
                    // Handle case where worker was terminated between checks
                    console.error("Error posting message to worker:", error);
                    pendingOperationsRef.current.delete(reject);
                    setIsLoading(false);
                    reject(error);
                }
            });
        },
        []
    );

    return {
        processData,
        isLoading,
        error,
        setError,
    };
};
